/**
 * Body of the "User Details" modal on the Login Analytics page: summary stat
 * tiles, a row of login-day pills, and a recent-logins table.
 */
import { formatNumber } from '../lib/utils';
import { formatDateTime, formatMonthDay } from '@/lib/utils/dates';
import { Stat } from './parts';
import type { LoginUserDetail } from '../types';

const TH_CLASS =
  'border-b border-[color:var(--gray-200)] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--text-dark)]';

export default function LoginUserDetailModalBody({
  detail,
}: {
  detail: LoginUserDetail;
}): JSX.Element {
  // Both lists are capped at the 30 / 50 most recent rows the modal shows.
  const loginDays = detail.login_days.slice(0, 30);
  const recentLogins = detail.recent_logins.slice(0, 50);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Logins" value={formatNumber(detail.total_logins)} />
        <Stat label="First Login" value={formatDateTime(detail.first_login)} small />
        <Stat label="Last Login" value={formatDateTime(detail.last_login)} small />
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-[color:var(--text-dark)]">
          Login Days (Last 30)
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {loginDays.length === 0 ? (
            <span className="text-sm text-[color:var(--text-dark)]">No login days recorded</span>
          ) : (
            loginDays.map((d) => (
              <span
                key={d}
                className="inline-flex items-center rounded-full bg-[var(--accent-light)] px-2.5 py-0.5 text-xs text-[color:var(--accent)]"
              >
                {formatMonthDay(d)}
              </span>
            ))
          )}
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-[color:var(--text-dark)]">Recent Logins</h4>
        <div className="overflow-x-auto rounded-md border border-[color:var(--gray-200)]">
          <table className="min-w-full border-collapse text-sm font-[var(--font-body)]">
            <thead>
              <tr className="bg-[var(--light-bg)]">
                <th className={TH_CLASS}>Timestamp</th>
                <th className={TH_CLASS}>IP Address</th>
                <th className={TH_CLASS}>Method</th>
              </tr>
            </thead>
            <tbody>
              {recentLogins.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-4 text-center text-sm text-[color:var(--text-dark)]"
                  >
                    No recent logins
                  </td>
                </tr>
              ) : (
                recentLogins.map((log, idx) => (
                  <tr
                    key={`${log.timestamp}-${idx}`}
                    className="border-b border-[color:var(--gray-200)] last:border-b-0"
                  >
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-3 py-2 text-[color:var(--text-dark)]">
                      {log.auth_method || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
